import { createContext, useContext, useState, useCallback } from "react";

export type SidebarVersion = "v1" | "v2";

const SIDEBAR_VERSION_KEY = "sidebar_version";

function getStoredVersion(): SidebarVersion {
  try {
    const stored = localStorage.getItem(SIDEBAR_VERSION_KEY);
    if (stored === "v1" || stored === "v2") return stored;
  } catch {}
  return "v2"; // default to new layout
}

interface SidebarContextProps {
  collapsed: boolean;
  /** True when sidebar was auto-expanded (click on collapsed icon) */
  autoExpanded: boolean;
  /** Which sidebar layout to use */
  sidebarVersion: SidebarVersion;
  toggle: () => void;
  expand: () => void;
  collapse: () => void;
  collapseIfAuto: () => void;
  setSidebarVersion: (version: SidebarVersion) => void;
}

const SidebarContext = createContext<SidebarContextProps | undefined>(
  undefined,
);

export const SidebarProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [collapsed, setCollapsed] = useState(false);
  const [autoExpanded, setAutoExpanded] = useState(false);
  const [sidebarVersion, _setSidebarVersion] =
    useState<SidebarVersion>(getStoredVersion);

  const toggle = useCallback(() => {
    setCollapsed((prev) => {
      if (!prev) {
        setAutoExpanded(false);
      }
      return !prev;
    });
  }, []);

  const expand = useCallback(() => {
    setCollapsed(false);
    setAutoExpanded(true);
  }, []);

  const collapse = useCallback(() => {
    setCollapsed(true);
    setAutoExpanded(false);
  }, []);

  const collapseIfAuto = useCallback(() => {
    setAutoExpanded((prev) => {
      if (prev) {
        setCollapsed(true);
      }
      return false;
    });
  }, []);

  const setSidebarVersion = useCallback((version: SidebarVersion) => {
    _setSidebarVersion(version);
    try {
      localStorage.setItem(SIDEBAR_VERSION_KEY, version);
    } catch {}
  }, []);

  return (
    <SidebarContext.Provider
      value={{
        collapsed,
        autoExpanded,
        sidebarVersion,
        toggle,
        expand,
        collapse,
        collapseIfAuto,
        setSidebarVersion,
      }}
    >
      {children}
    </SidebarContext.Provider>
  );
};

export const useSidebar = () => {
  const context = useContext(SidebarContext);
  if (!context) {
    throw new Error("useSidebar must be used within a SidebarProvider");
  }
  return context;
};
